import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import Image from "../Image";
import styles from "./Embed.css";

class Embed extends Component {
  calculateAspectRatio = () => {
    const { oembed, type } = this.props.block.data;
    let aspectRatio;
    if (oembed.height && oembed.width) {
      // if there's a width and height, then create a placeholder
      aspectRatio = oembed.height / oembed.width;
    }
    if (type === "jwplayer-video") {
      // JW player's metadata includes some, but not all of the controls' height
      // Hardcoding the height, 44px, ensures controls are always visible
      aspectRatio = (oembed.height + 44) / oembed.width;
    }
    return aspectRatio;
  };

  calculateContainerHeight = embedAspectRatio => {
    const { oembed, type } = this.props.block.data;
    // Art19 podcast embeds have a hard-coded height that our typical container height logic does not apply to
    const height =
      type === "art19" ? `${oembed.height}px` : `${embedAspectRatio * 100}%`;
    return height;
  };

  renderEmbed = (isFullbleed, embedAspectRatio) => {
    // see handleEmbed in utils/embed.js for additional rendering
    const { oembed, type } = this.props.block.data;
    return (
      <figure>
        <div
          className={classNames(
            styles.embedContainer,
            {
              [styles.responsiveIframe]: embedAspectRatio,
            },
            {
              [styles.fullBleed]: isFullbleed,
            },
            `block-${this.props.block.key}`
          )}
          style={
            embedAspectRatio && {
              paddingBottom: this.calculateContainerHeight(embedAspectRatio),
            }
          }
          {...this.props.isClient && {
            dangerouslySetInnerHTML: {
              __html: oembed.html,
            },
          }}
        />
        <figcaption
          className={classNames({
            [styles.hide]: type !== "axios-visual",
          })}
        >
          {this.props.children}
        </figcaption>
      </figure>
    );
  };

  renderPhoto = () => {
    const { oembed } = this.props.block.data;
    const blockData = {
      data: {
        image_id: this.props.block.key,
        src: oembed.url,
        alt_text: oembed.title,
      },
    };
    return (
      <figure>
        <Image block={blockData}> {this.props.children} </Image>
      </figure>
    );
  };

  renderJWVideo = (isFullbleed, embedAspectRatio) => {
    // div#botr is required for rendering JW videos
    // BOTR is a proprietary JW API for loading videos, "Bits on the Run" (︶︿︶ )
    // see handleEmbed in utils/embed.js for additional rendering
    const { oembed, config = {} } = this.props.block.data;
    const playerId = config.autoplay
      ? oembed.autoplay_player_id
      : oembed.default_player_id;
    return (
      <figure>
        <div
          className={classNames(
            styles.embedContainer,
            {
              [styles.responsiveIframe]: embedAspectRatio,
            },
            {
              [styles.fullBleed]: isFullbleed,
            },
            `block-${this.props.block.key}`
          )}
          style={
            embedAspectRatio && {
              paddingBottom: this.calculateContainerHeight(embedAspectRatio),
            }
          }
        >
          <div id={`jw-${oembed.media_id}`}>
            <div id={`botr_${oembed.media_id}_${playerId}_div`} />
          </div>
        </div>
        {this.props.children}
      </figure>
    );
  };

  render() {
    const { oembed, type } = this.props.block.data;
    if (!oembed) return null;
    let isFullbleed = oembed.is_fullbleed;
    let embedAspectRatio = this.calculateAspectRatio(oembed, type);
    if (type === "jwplayer-video" || type === "youtube") {
      // Youtube and JW Player videos should stretch edge-to-edge
      // The following boolean adds a CSS classname to do that
      isFullbleed = true;
    }

    let renderMethod;
    switch (type) {
      case "giphy":
        renderMethod = this.renderPhoto;
        break;
      case "jwplayer-video":
        renderMethod = this.renderJWVideo;
        break;
      default:
        renderMethod = this.renderEmbed;
        break;
    }

    return (
      <div className={styles.root}>
        {renderMethod(isFullbleed, embedAspectRatio)}
      </div>
    );
  }
}

Embed.propTypes = {
  block: PropTypes.object.isRequired,
  isClient: PropTypes.bool,
};

export default Embed;
